import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, CheckCheck, X } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { resolveNotifLink } from "../lib/notifLinks";

/**
 * VenturaZ Notification Bell.
 * Dropdown popover with the last 10 notifications. Live unread badge polled
 * every 30s. Click-outside dismiss, per-item mark-read on click, and a
 * "Mark all read" action.
 */
export default function NotificationBell({ viewAllHref = null, testIdPrefix = "notif" }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role;
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const refreshBadge = () => {
    api.get("/notifications/unread-count")
      .then((r) => setUnread(Math.min(r.data?.count || 0, 999)))
      .catch(() => {});
  };
  const loadItems = () => {
    setLoading(true);
    api.get("/notifications?limit=10&include_archived=false")
      .then((r) => {
        setItems(r.data?.items || []);
        setUnread(Math.min(r.data?.unread_count || 0, 999));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refreshBadge();
    const iv = setInterval(refreshBadge, 30000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    if (!open) return;
    loadItems();
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const markOneRead = async (nid) => {
    try { await api.post(`/notifications/${nid}/read`); } catch (e) { /* ignore */ }
    setItems((cur) => cur.map((n) =>
      (n.notif_id === nid || n.notification_id === nid) ? { ...n, read: true } : n,
    ));
    refreshBadge();
  };
  const markAllRead = async () => {
    try { await api.post("/notifications/read-all"); } catch (e) { /* ignore */ }
    setItems((cur) => cur.map((n) => ({ ...n, read: true })));
    setUnread(0);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-md hover:bg-navy/[0.04] text-ink-soft hover:text-ink transition-colors"
        title="Notifications"
        aria-label="Notifications"
        data-testid={`${testIdPrefix}-bell`}
      >
        <Bell size={18} />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-gold text-navy text-[10px] font-heading font-bold"
            data-testid={`${testIdPrefix}-count`}
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="fixed left-2 right-2 top-[64px] sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-[360px] sm:max-w-[96vw] bg-white border border-navy/15 rounded-lg shadow-2xl z-[100] overflow-hidden"
          data-testid={`${testIdPrefix}-popover`}
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-navy/10 bg-navy/[0.02]">
            <div className="overline">NOTIFICATIONS</div>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-[11px] font-mono uppercase tracking-wider text-ink-muted hover:text-gold flex items-center gap-1 px-2 py-1"
                  data-testid={`${testIdPrefix}-mark-all-read`}
                >
                  <CheckCheck size={11} /> Mark all read
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="p-1 text-ink-muted hover:text-ink"
                aria-label="Close"
                data-testid={`${testIdPrefix}-close`}
              >
                <X size={14} />
              </button>
            </div>
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {loading ? (
              <div className="p-6 text-xs font-mono text-ink-muted text-center" data-testid={`${testIdPrefix}-loading`}>Loading…</div>
            ) : items.length === 0 ? (
              <div className="p-6 text-center text-ink-muted text-sm" data-testid={`${testIdPrefix}-empty`}>
                <Bell size={22} className="mx-auto mb-2 text-ink-faint" />
                No notifications yet.
              </div>
            ) : (
              items.map((n) => {
                const nid = n.notif_id || n.notification_id;
                const isUnread = !n.read;
                const linkTo = resolveNotifLink(n, role);
                return (
                  <button
                    key={nid}
                    onClick={() => {
                      markOneRead(nid);
                      if (linkTo) {
                        setOpen(false);
                        navigate(linkTo);
                      }
                    }}
                    className={`w-full text-left px-3 py-2.5 border-b border-navy/5 hover:bg-navy/[0.02] transition ${
                      isUnread ? "bg-gold/[0.06]" : ""
                    }`}
                    data-testid={`${testIdPrefix}-item-${nid}`}
                  >
                    <div className="flex items-start gap-2">
                      {isUnread && (
                        <span className="mt-1 w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" aria-hidden="true" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-ink truncate">{n.title || n.message || n.body || "Notification"}</div>
                        <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted mt-0.5">
                          {(n.type || n.kind || "notification").replace(/_/g, " ")}
                          {n.created_at ? ` · ${(n.created_at || "").slice(0, 16).replace("T", " ")}` : ""}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {viewAllHref && (
            <Link
              to={viewAllHref}
              onClick={() => setOpen(false)}
              className="block text-center px-3 py-2 border-t border-navy/10 text-[11px] font-mono uppercase tracking-wider text-ink-muted hover:text-gold hover:bg-navy/[0.02]"
              data-testid={`${testIdPrefix}-view-all`}
            >
              View all
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
